// Admin → User → Access: the READ-ONLY overview at the top of the tab (29 Oct 2026 redesign). It answers "what can this
// account do, in which mode, and if not, what is missing" from the same keys, roles and server switches the backend
// enforces (options_access.FLAGS / deployment(), broker_accounts shadow/live, risk_gate segments and limits, each
// engine's order port). Presentation only: every order is still checked by the server. Editing stays in the sections
// below (Futures authorisation, Options keys, Segments, Limits) - unchanged endpoints, unchanged audit.
import React from "react";

/** One requirement: {label, ok, kind: "key" | "role" | "server" | "product" | "toggle"} */
const req = (label, ok, kind = "key") => ({ label, ok: !!ok, kind });

/** Cell state from its requirements. `own` = the strategy's own key for this mode (configured). */
export function cellState(reqs, own, supported = true) {
  if (!supported) return { state: "NA", missing: [] };
  const missing = reqs.filter((r) => !r.ok);
  if (!missing.length) return { state: "ON", missing };
  return { state: own ? "BLOCKED" : "OFF", missing };
}

/**
 * Strategy x mode matrix for one linked account. `opt`: the /admin/options-access account row; `fut`: the broker account
 * row (futures shadow_enabled / live_enabled); `dep`: options_access.deployment(); `user`: roles / status.
 */
export function strategyMatrix({ opt, fut, dep, user }) {
  const a = { ...(fut || {}), ...(opt || {}) };
  const eff = (k) => !!(a.effective?.[k]?.enabled);
  const roles = user?.roles || [];
  const liveRole = req("LIVE_TRADER role", roles.includes("LIVE_TRADER"), "role");
  const traderRole = req("TRADER role", roles.includes("TRADER") || roles.includes("LIVE_TRADER") || roles.includes("ADMIN"), "role");
  const products = a.effective_trading_products || "FUTURES_ONLY";
  const futProd = req("Trading products include Futures", products !== "OPTIONS_ONLY", "product");
  const optProd = req("Trading products include Options", products !== "FUTURES_ONLY", "product");
  const linked = req("Zerodha account linked", a.link_status === "ACTIVE", "key");
  const shadowSrv = req("Server: SHADOW_TRADING_ENABLED", dep.shadow_trading_enabled, "server");
  const liveSrv = req("Server: LIVE_TRADING_ENABLED", dep.live_trading_enabled, "server");
  const hedgeSrv = [req("Server: OPTIONS_HEDGE_ENABLED", dep.options_hedge_enabled, "server"),
    req("Server: OPTIONS_HEDGE_BROKER_WRITES", dep.options_hedge_broker_writes, "server")];
  const optLiveSrv = req("Server: OPTIONS_LIVE_TRADING_ENABLED", dep.options_live_trading_enabled, "server");
  const k = (label, on) => req(label, on, "key");

  const optShadow = [linked, optProd, k("Options Hedging", eff("HEDGE")), k("Options SHADOW", eff("SHADOW")), ...hedgeSrv, shadowSrv];
  const optLive = [linked, optProd, liveRole, k("Options Hedging", eff("HEDGE")), k("Options LIVE", eff("LIVE")), ...hedgeSrv, optLiveSrv, liveSrv];
  const sellShadow = cellState(optShadow, eff("SHADOW"));
  const sellLiveReqs = [...optLive, k("Options LIVE SELL", eff("SELL_LIVE"))];
  const sellLive = cellState(sellLiveReqs, eff("SELL_LIVE"));
  const rescueSrv = req("Server: OPTIONS_RESCUE_ENABLED", dep.options_rescue_enabled, "server");
  const toggle = req("Rescue ON for the trade or pair (per trade / per pair)", true, "toggle");

  const rows = [
    { key: "FUTURES", label: "Futures (calendar spreads)", note: "PAPER runs on simulated capital for any TRADER; SHADOW / LIVE need the account's Futures keys",
      PAPER: cellState([traderRole], true),
      SHADOW: cellState([linked, futProd, k("Futures SHADOW", a.shadow_enabled), shadowSrv], !!a.shadow_enabled),
      LIVE: cellState([linked, futProd, liveRole, k("Futures LIVE", a.live_enabled), liveSrv], !!a.live_enabled) },
    { key: "BUY_OPTIONS", label: "BUY Options (ITM CE + PE pairs)", note: "Options Hedging engine",
      PAPER: cellState([], false, false), SHADOW: cellState(optShadow, eff("SHADOW")), LIVE: cellState(optLive, eff("LIVE")) },
    { key: "SELL_OPTIONS", label: "SELL Options / SELL pairs", note: "Options Hedging engine; LIVE SELL has its own key on top of Options LIVE",
      PAPER: cellState([], false, false), SHADOW: sellShadow, LIVE: sellLive },
    { key: "IRON_CONDOR", label: "Iron Condor", note: "Separate 4-leg engine",
      PAPER: cellState([], false, false),
      SHADOW: cellState([linked, optProd, k("Options SHADOW", eff("SHADOW")), k("Iron Condor SHADOW", eff("IC_SHADOW")),
        req("Server: IRON_CONDOR_ENABLED", dep.iron_condor_enabled, "server"), req("Server: IRON_CONDOR_BROKER_WRITES", dep.iron_condor_broker_writes, "server"), shadowSrv], eff("IC_SHADOW")),
      LIVE: cellState([linked, optProd, liveRole, k("Options LIVE", eff("LIVE")), k("Iron Condor LIVE", eff("IC_LIVE")),
        req("Server: IRON_CONDOR_ENABLED", dep.iron_condor_enabled, "server"), req("Server: IRON_CONDOR_BROKER_WRITES", dep.iron_condor_broker_writes, "server"),
        req("Server: IRON_CONDOR_LIVE_ENABLED", dep.iron_condor_live_enabled, "server"), optLiveSrv, liveSrv], eff("IC_LIVE")) },
    { key: "BOX_SPREAD", label: "Box Spread", note: "Separate 4-leg engine; its own SHADOW key (Iron Condor SHADOW does not authorise it)",
      PAPER: cellState([], false, false),
      SHADOW: cellState([linked, optProd, k("Options SHADOW", eff("SHADOW")), k("Box Spread SHADOW", eff("BOX_SHADOW")),
        req("Server: BOX_SPREAD_ENABLED", dep.box_spread_enabled, "server"), req("Server: BOX_SPREAD_BROKER_WRITES", dep.box_spread_broker_writes, "server"), shadowSrv], eff("BOX_SHADOW")),
      LIVE: cellState([linked, optProd, liveRole, k("Options LIVE", eff("LIVE")), k("Box Spread LIVE", eff("BOX_LIVE")),
        req("Server: BOX_SPREAD_ENABLED", dep.box_spread_enabled, "server"), req("Server: BOX_SPREAD_BROKER_WRITES", dep.box_spread_broker_writes, "server"),
        req("Server: BOX_SPREAD_LIVE_ENABLED", dep.box_spread_live_enabled, "server"), optLiveSrv, liveSrv], eff("BOX_LIVE")) },
    { key: "RESCUE", label: "Dynamic Adaptive Rescue", note: "Protective BUY legs on SELL pairs. SHADOW has NO separate account key: it follows SELL Options SHADOW. LIVE needs its own key.",
      PAPER: cellState([], false, false),
      SHADOW: cellState([...optShadow.map((r) => (r.kind === "key" && r.label === "Options SHADOW" ? { ...r, label: "Options SHADOW (SELL Options SHADOW)" } : r)), rescueSrv, toggle], eff("SHADOW")),
      LIVE: cellState([...sellLiveReqs, k("Dynamic Adaptive Rescue LIVE", !!a.rescue_live_enabled), rescueSrv, toggle], !!a.rescue_live_enabled) },
  ];
  return rows;
}

const CHIP = {
  ON: ["Enabled", "bg-[#00C805]/12 text-[#008a04] border-[#00C805]/40"],
  OFF: ["Disabled", "bg-[var(--term-s3)] text-[var(--term-secondary)] border-[var(--term-border-strong)]"],
  BLOCKED: ["Blocked", "bg-[#FFCC00]/15 text-[#8a6500] border-[#FFCC00]/50"],
  NA: ["Not supported", "bg-transparent text-[var(--term-muted)] border-dashed border-[var(--term-border)]"],
};

export function Chip({ state, testId }) {
  const [label, cls] = CHIP[state] || CHIP.OFF;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap ${cls}`} data-testid={testId}>
    <span aria-hidden>{state === "ON" ? "●" : state === "BLOCKED" ? "▲" : state === "NA" ? "–" : "○"}</span>{label}</span>;
}

function Section({ n, title, sub, children, testId }) {
  return (
    <section className="rounded-md border border-[var(--term-border)] bg-[var(--term-s1)] p-4 space-y-3" data-testid={testId}>
      <div>
        <h3 className="text-sm font-bold text-[var(--term-primary)]"><span className="text-[var(--term-muted)] mr-1.5">{n}.</span>{title}</h3>
        {sub && <p className="text-xs text-[var(--term-secondary)] mt-0.5">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

function Fact({ label, value, ok, testId }) {
  return (
    <div className="rounded-md border border-[var(--term-border)] px-3 py-2" data-testid={testId}>
      <div className="text-[11px] text-[var(--term-secondary)]">{label}</div>
      <div className={`text-sm font-semibold ${ok === true ? "text-[#008a04]" : ok === false ? "text-[#8a6500]" : ""}`}>{value}</div>
    </div>
  );
}

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—");

function MatrixCell({ cell, testId }) {
  const [open, setOpen] = React.useState(false);
  const needs = cell.missing || [];
  return (
    <td className="px-3 py-2 align-top">
      <button type="button" className="text-left disabled:cursor-default" disabled={!needs.length} onClick={() => setOpen(!open)}
        aria-expanded={open} title={needs.length ? "Show what is missing" : undefined} data-testid={testId}>
        <Chip state={cell.state} />
        {needs.length > 0 && <span className="ml-1 text-[11px] text-[var(--term-secondary)]">{needs.length} missing {open ? "▾" : "▸"}</span>}
      </button>
      {open && <ul className="mt-1.5 space-y-0.5 text-[11px]" data-testid={`${testId}-missing`}>
        {needs.map((m) => <li key={m.label} className="text-[#8a6500]">✗ {m.label}{m.kind === "server" ? " (server switch - deployment, not an admin toggle)" : ""}</li>)}
      </ul>}
    </td>
  );
}

const SEG_ROWS = [["NFO_INDEX", "NFO index options"], ["NFO_STOCK", "NFO stock options"], ["MCX", "MCX options"]];

function segCell(seg, env, lim) {
  const s = lim?.[`segments_${env}`];
  if (!s) return { state: "OFF", text: "—" };
  if (s.source === "LEGACY") {
    if (seg === "MCX") {
      const mode = lim.options_mcx_access || "NONE";
      return mode === "NONE" ? { state: "OFF", text: "Off" } : { state: "ON", text: mode === "ALL_MCX_OPTIONS" ? "All" : `Selected: ${(lim.options_mcx_allowlist || []).join(", ")}` };
    }
    if (seg === "NFO_STOCK") {
      const all = (lim.options_instrument_access || "ALL_NFO_STOCK_OPTIONS") === "ALL_NFO_STOCK_OPTIONS";
      return { state: all || (lim.options_allowlist || []).length ? "ON" : "OFF", text: all ? "All (per-underlying rule)" : `Selected: ${(lim.options_allowlist || []).join(", ") || "none"}` };
    }
    return { state: "OFF", text: "Not in the per-underlying rules" };
  }
  const on = !!s.switches?.[seg];
  return { state: on ? "ON" : "OFF", text: on ? (s.source === "SHADOW_DEFAULT" ? "Allowed (default: all)" : "Allowed") : "Switched off" };
}

function futSegCell(seg, env, fs) {
  const sw = fs?.[env];
  if (!sw) return { state: "ON", text: "Allowlist decides (no segment rule)" };
  return sw[seg] ? { state: "ON", text: "Allowed" } : { state: "OFF", text: "Switched off" };
}

export default function AccessOverview({ user, futuresAccounts, optionsData, analysis }) {
  const dep = optionsData?.deployment || {};
  const optRows = (optionsData?.accounts || []).filter((a) => a.owner_user_id === user.id);
  const active = optRows.find((a) => a.link_status === "ACTIVE") || optRows[0];
  const fut = (futuresAccounts || []).find((f) => f.id === active?.id) || (futuresAccounts || [])[0];
  if (!active && !fut) {
    return <Section n={1} title="Account & broker status" testId="access-overview">
      <div className="text-sm text-[var(--term-secondary)]">No Zerodha account is linked to this user. They connect it from Broker Session; until then no trading mode except Futures PAPER is available.</div>
    </Section>;
  }
  const rows = strategyMatrix({ opt: active, fut, dep, user });
  const lim = active?.options_limits || {};
  const roles = user.roles || [];
  const connected = fut?.session_status === "CONNECTED";
  const anyOn = (mode) => rows.some((r) => r[mode].state === "ON");
  const products = { FUTURES_ONLY: "Futures only", OPTIONS_ONLY: "Options only", BOTH: "Futures and Options" }[active?.effective_trading_products] || "—";
  const e = (k) => !!active?.effective?.[k]?.enabled;
  const scanners = [
    ["Options Analysis (Scanner)", e("ANALYSIS"), analysis == null ? "checking the options worker…" : !analysis.reachable ? "options worker unreachable" : analysis.enabled ? "worker running" : "worker switched off (OPTIONS_ANALYSIS_ENABLED)"],
    ["Iron Condor scanner", e("IRON_CONDOR"), dep.iron_condor_scanner_enabled ? "server scanner on" : "server: IRON_CONDOR_SCANNER_ENABLED off"],
    ["Box Spread scanner", e("BOX_SCANNER"), active?.effective?.BOX_SCANNER?.source === "FOLLOWS_IRON_CONDOR_SCANNER" ? "not set: follows the Iron Condor scanner" : "own key"],
  ];

  return (
    <div className="space-y-4" data-testid="access-overview">
      <Section n={1} title="Account & broker status" sub="Who this is and whether the linked Zerodha account can trade right now." testId="access-status">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
          <Fact label="User" value={`${user.status} · ${roles.join(", ") || "no role"}`} ok={user.status === "ACTIVE"} testId="access-fact-user" />
          <Fact label="Linked broker" value={`Zerodha · ${active?.broker_user_id || fut?.broker_user_id || "—"} (${(active?.link_status || fut?.link_status || "—").toLowerCase()})`} ok={(active?.link_status || fut?.link_status) === "ACTIVE"} testId="access-fact-broker" />
          <Fact label="Broker session" value={connected ? `Connected · last ${when(fut?.last_connected_at)}` : `Not connected · last ${when(fut?.last_connected_at)}`} ok={connected} testId="access-fact-session" />
          <Fact label="Trading products" value={products} testId="access-fact-products" />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="access-readiness">
          <span className="font-semibold">Effective readiness:</span>
          {["PAPER", "SHADOW", "LIVE"].map((m) => <span key={m} className="flex items-center gap-1">{m} <Chip state={anyOn(m) ? "ON" : "OFF"} testId={`access-ready-${m}`} /></span>)}
          {!connected && <span className="text-[#8a6500]">SHADOW / LIVE orders also need a connected broker session.</span>}
        </div>
      </Section>

      <Section n={2} title="Trading modes" sub="What each mode does. A strategy only runs in the modes its engine supports (section 3)." testId="access-modes">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <div className="rounded-md border border-[var(--term-border)] p-3 space-y-1"><b className="text-sm">PAPER</b>
            <div className="text-[var(--term-secondary)]">Simulated capital and fills, no broker orders. <b>Futures only</b> - the options engines have no PAPER mode.</div></div>
          <div className="rounded-md border border-dashed border-[var(--term-border-strong)] p-3 space-y-1"><b className="text-sm">Local SIMULATOR</b>
            <div className="text-[var(--term-secondary)]">Development environment on a developer's machine: its own backend, database and simulated exchange (no Zerodha). Not an account permission and never on the server.</div></div>
          <div className="rounded-md border border-[#FFCC00]/50 bg-[#FFCC00]/5 p-3 space-y-1"><b className="text-sm">SHADOW</b>
            <div className="text-[var(--term-secondary)]"><b className="text-[#8a6500]">Sends REAL order requests to Zerodha</b> on an unfunded account; they are expected to be rejected and the strategy continues on a simulated fill. Server: {dep.shadow_trading_enabled ? "on" : "off"}.</div></div>
          <div className="rounded-md border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3 space-y-1"><b className="text-sm">LIVE</b>
            <div className="text-[var(--term-secondary)]">Real orders, real money. Needs the LIVE_TRADER role, the account's LIVE keys and the server's LIVE switches. Server: {dep.live_trading_enabled ? "on" : "off"}. An account is never SHADOW and LIVE at once.</div></div>
        </div>
      </Section>

      <Section n={3} title="Trading strategies" sub="Effective access per strategy and mode. Click a Blocked or Disabled cell to see exactly what is missing." testId="access-strategies">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[640px]" data-testid="access-strategy-matrix">
            <thead><tr className="text-left text-xs text-[var(--term-secondary)] border-b border-[var(--term-border)]">
              <th className="px-3 py-2 font-semibold">Strategy</th><th className="px-3 py-2 font-semibold">PAPER</th>
              <th className="px-3 py-2 font-semibold">SHADOW</th><th className="px-3 py-2 font-semibold">LIVE</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.key} className="border-b border-[var(--term-border)]" data-testid={`access-row-${r.key}`}>
                <td className="px-3 py-2 align-top"><div className="font-semibold">{r.label}</div><div className="text-[11px] text-[var(--term-secondary)] max-w-xs">{r.note}</div></td>
                {["PAPER", "SHADOW", "LIVE"].map((m) => <MatrixCell key={m} cell={r[m]} testId={`access-${r.key}-${m}`} />)}
              </tr>))}</tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] text-[var(--term-secondary)]">
          <span className="flex items-center gap-1"><Chip state="ON" /> every key, role and server switch is on</span>
          <span className="flex items-center gap-1"><Chip state="BLOCKED" /> its own key is on, but a prerequisite is missing</span>
          <span className="flex items-center gap-1"><Chip state="OFF" /> its own key is off</span>
          <span className="flex items-center gap-1"><Chip state="NA" /> the engine has no such mode</span>
        </div>
        <details className="rounded-md border border-[var(--term-border)] p-3 text-xs" data-testid="access-rescue-explainer">
          <summary className="cursor-pointer font-semibold">How Dynamic Adaptive Rescue is enabled</summary>
          <ul className="mt-2 space-y-1 list-disc pl-5 text-[var(--term-secondary)]">
            <li><b>SHADOW:</b> there is <b>no separate Rescue SHADOW account key</b>. It runs on any account where SELL Options SHADOW is enabled, once the server switch is on and Rescue is turned on for the trade or pair.</li>
            <li><b>LIVE:</b> the account needs Options LIVE, Options LIVE SELL <b>and</b> the separate <i>Dynamic Adaptive Rescue LIVE</i> key (below, under LIVE execution keys). That key never affects SHADOW.</li>
            <li><b>Server switch <code>OPTIONS_RESCUE_ENABLED</code></b> ({dep.options_rescue_enabled ? "on" : "off"} on this server): off means no rescue order in any mode - pairs show "blocked".</li>
            <li><b>Per trade / per pair:</b> off by default. Tick "Dynamic Adaptive Rescue" on the SELL ticket (every pair of that trade starts with it on) or switch it per pair in the pair table. Switching off never cancels a buy already sent.</li>
          </ul>
        </details>
      </Section>

      <Section n={4} title="Analysis and scanners" sub="Read-only: scanning never places an order. Execution access is in section 3." testId="access-scanners">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {scanners.map(([label, on, why]) => (
            <div key={label} className="rounded-md border border-[var(--term-border)] px-3 py-2 flex items-center gap-2" data-testid={`access-scanner-${label.split(" ")[0]}`}>
              <div className="mr-auto"><div className="text-sm font-semibold">{label}</div><div className="text-[11px] text-[var(--term-secondary)]">{why}</div></div>
              <Chip state={on ? "ON" : "OFF"} />
            </div>))}
        </div>
      </Section>

      <Section n={5} title="Segments and instruments" sub="Where new entries are allowed, per mode (risk_gate). Exits and square-offs are never refused by these rules." testId="access-segments">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[560px]" data-testid="access-segment-table">
            <thead><tr className="text-left text-xs text-[var(--term-secondary)] border-b border-[var(--term-border)]">
              <th className="px-3 py-2">Segment</th><th className="px-3 py-2">SHADOW</th><th className="px-3 py-2">LIVE</th></tr></thead>
            <tbody>
              {SEG_ROWS.map(([seg, label]) => {
                const s = segCell(seg, "shadow", lim), l = segCell(seg, "live", lim);
                return <tr key={seg} className="border-b border-[var(--term-border)]"><td className="px-3 py-2 font-semibold">{label}</td>
                  <td className="px-3 py-2"><Chip state={s.state} /> <span className="text-[11px] text-[var(--term-secondary)]">{s.text}</span></td>
                  <td className="px-3 py-2"><Chip state={l.state} /> <span className="text-[11px] text-[var(--term-secondary)]">{l.text}</span></td></tr>;
              })}
              {[["NFO", "NFO futures"], ["MCX", "MCX futures"]].map(([seg, label]) => {
                const s = futSegCell(seg, "shadow", active?.futures_segments), l = futSegCell(seg, "live", active?.futures_segments);
                return <tr key={seg} className="border-b border-[var(--term-border)]"><td className="px-3 py-2 font-semibold">{label}</td>
                  <td className="px-3 py-2"><Chip state={s.state} /> <span className="text-[11px] text-[var(--term-secondary)]">{s.text}</span></td>
                  <td className="px-3 py-2"><Chip state={l.state} /> <span className="text-[11px] text-[var(--term-secondary)]">{l.text}</span></td></tr>;
              })}
            </tbody>
          </table>
        </div>
        {lim.segments_live?.source === "LEGACY" && <div className="text-[11px] text-[var(--term-secondary)]">LIVE options still use the per-underlying rules (no LIVE segment switches set).</div>}
      </Section>

      <Section n={6} title="Limits and risk" sub="Caps applied to every options order of this account. Separate from the authorisation keys above." testId="access-limits">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
          <Fact label="Max lots per order" value={lim.options_max_lots_per_order ?? 1} testId="access-limit-lots" />
          <Fact label="Max pairs per trade" value={lim.options_max_pairs_per_trade ?? 1} testId="access-limit-pairs" />
          <Fact label="Max open trades" value={lim.options_max_open_trades ?? 1} testId="access-limit-trades" />
          <Fact label="Daily loss limit" value={Number(lim.options_daily_loss_limit) ? `₹${Number(lim.options_daily_loss_limit).toLocaleString("en-IN")}` : "Not set (SHADOW only; LIVE refuses)"}
            ok={Number(lim.options_daily_loss_limit) ? undefined : false} testId="access-limit-loss" />
        </div>
        <div className="text-[11px] text-[var(--term-secondary)]">LIVE capital limits are the account's LIVE limits (futures and options together, Risk controls). Edit these caps below under "Segments, instruments and limits".</div>
      </Section>
    </div>
  );
}
